import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import { ItemType } from "@ofqual/portal-components/components/types";
import { LookupTracker } from "@ofqual/portal-core/core/types";
import { useState } from "react";

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

type AddStaffButtonAndDialogProps = {
  disabled: boolean;
  lookupTracker: LookupTracker;
  teamLookups: ItemType[];
  userLookups: (ItemType & { teams: { id: string }[] })[];
  onSelect: (value: string) => void;
};

export const AddStaffButtonAndDialog: React.FC<AddStaffButtonAndDialogProps> = ({
  disabled,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        color="primary"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        data-test="add-staff-button"
      >
        Add Staff
      </Button>

      {isOpen && <AddStaffDialog {...props} onClose={() => setIsOpen(false)} />}
    </>
  );
};

const AddStaffDialog: React.FC<
  Omit<AddStaffButtonAndDialogProps, "disabled"> & { onClose: () => void }
> = ({
  onClose,
  lookupTracker,
  userLookups: users,
  teamLookups: teams,
  onSelect,
}) => {
  const classes = useStyles();

  const [selectedTeam, setSelectedTeam] = useState<string | undefined>(
    undefined
  );
  const [selectedUser, setSelectedUser] = useState<
    string | "ALL_TEAM" | undefined
  >(undefined);

  const handleSelect = () => {
    if (selectedTeam && selectedUser) {
      onSelect(selectedUser === "ALL_TEAM" ? selectedTeam : selectedUser);
    }
    onClose();
  };

  const handleSetSelectedTeam = (teamId: string) => {
    setSelectedTeam(teamId);
    setSelectedUser(undefined);
  };

  const handleSetSelectedUser = (userId: string) => {
    setSelectedUser(userId);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add staff permission - select a team or user</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Select
              label="Select team"
              lookupTracker={lookupTracker}
              value={selectedTeam}
              onChange={(val) => handleSetSelectedTeam(val.target.value)}
              fullWidth
              datatest="select-team-field"
            >
              {teams}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <Select
              label={
                selectedTeam === undefined
                  ? "Please Select a team first"
                  : "Select a user"
              }
              lookupTracker={lookupTracker}
              value={selectedUser}
              onChange={(val) => handleSetSelectedUser(val.target.value)}
              fullWidth
              disabled={selectedTeam === null}
              datatest="select-user-field"
            >
              {[
                { id: "ALL_TEAM", label: "All team users" },
                ...users.filter((user) =>
                  user.teams.some((team) => team.id === selectedTeam)
                ),
              ]}
            </Select>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          component="button"
          color="primary"
          onClick={onClose}
          data-test="ofqual-staff-close-button"
        >
          Close
        </Button>
        <Button
          component="button"
          color="primary"
          data-test="ofqual-staff-select-button"
          disabled={selectedUser === undefined}
          onClick={handleSelect}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};
